// Daily-tracking maths (SPEC.md §4.3, §5 Today/Health). Pure functions over plain rows.
import { addDays, format, parse } from 'date-fns';
import { compareSessions, type SessionRow } from './sessions';

const round1 = (value: number) => Math.round(value * 10) / 10;

/** Total fluid per local day for the `days` days ending on `today` ('yyyy-MM-dd'), oldest first. */
export function dailyFluidTotals(entries: { taken_at: string; amount_ml: number }[], days: number, today: string) {
  const totals = new Map<string, number>();
  for (const entry of entries) {
    const date = format(new Date(entry.taken_at), 'yyyy-MM-dd');
    totals.set(date, (totals.get(date) ?? 0) + entry.amount_ml);
  }
  const end = parse(today, 'yyyy-MM-dd', new Date());
  const result: { date: string; total: number }[] = [];
  for (let offset = days - 1; offset >= 0; offset--) {
    const date = format(addDays(end, -offset), 'yyyy-MM-dd');
    result.push({ date, total: totals.get(date) ?? 0 });
  }
  return result;
}

export interface WeightPoint {
  /** Epoch milliseconds. */
  t: number;
  weight: number;
  source: 'home' | 'pre' | 'post';
}

/** When a session's weight was taken: its start/end time on the session date, or a nominal hour. */
function sessionMoment(session: Pick<SessionRow, 'session_date' | 'start_time' | 'end_time'>, which: 'pre' | 'post') {
  const time = which === 'pre' ? (session.start_time ?? '08:00') : (session.end_time ?? session.start_time ?? '12:00');
  const moment = parse(`${session.session_date} ${time}`, 'yyyy-MM-dd HH:mm', new Date()).getTime();
  // A post weight with no end time still comes after the pre weight.
  return which === 'post' && !session.end_time ? moment + 4 * 3_600_000 : moment;
}

/** Home weights and session weights on one timeline, oldest first (SPEC.md §4.3 weight_entries). */
export function mergedWeights(home: { measured_at: string; weight_kg: number }[], sessions: SessionRow[]): WeightPoint[] {
  const points: WeightPoint[] = home.map((entry) => ({ t: new Date(entry.measured_at).getTime(), weight: entry.weight_kg, source: 'home' }));
  for (const session of sessions) {
    if (session.pre_weight_kg != null) points.push({ t: sessionMoment(session, 'pre'), weight: session.pre_weight_kg, source: 'pre' });
    if (session.post_weight_kg != null) points.push({ t: sessionMoment(session, 'post'), weight: session.post_weight_kg, source: 'post' });
  }
  return points.sort((a, b) => a.t - b.t);
}

/**
 * Weight gained since the last session ended: the latest weight measured after it minus its
 * post-weight. Null when there's no post-weight or nothing has been weighed since.
 */
export function gainSoFar(home: { measured_at: string; weight_kg: number }[], sessions: SessionRow[]) {
  const last = [...sessions].filter((session) => session.post_weight_kg != null).sort(compareSessions).pop();
  if (!last) return null;
  const after = sessionMoment(last, 'post');
  const latest = home
    .map((entry) => ({ t: new Date(entry.measured_at).getTime(), weight: entry.weight_kg }))
    .filter((entry) => entry.t > after)
    .sort((a, b) => b.t - a.t)[0];
  if (!latest) return null;
  return { gain: round1(latest.weight - last.post_weight_kg!), weight: latest.weight, sessionDate: last.session_date };
}

/** How often each symptom was logged, most frequent first (ties alphabetical). */
export function symptomFrequency(entries: { symptom: string; severity: number }[]) {
  const counts = new Map<string, { count: number; worst: number }>();
  for (const entry of entries) {
    const current = counts.get(entry.symptom) ?? { count: 0, worst: 0 };
    counts.set(entry.symptom, { count: current.count + 1, worst: Math.max(current.worst, entry.severity) });
  }
  return [...counts.entries()]
    .map(([symptom, value]) => ({ symptom, ...value }))
    .sort((a, b) => b.count - a.count || a.symptom.localeCompare(b.symptom));
}
